// src/components/onboarding/OnboardingDialog.tsx
//
// Shown to a registered user the first time they sign in (or any
// time they have no completed travelProfile and haven't already
// chosen to skip). Wraps the existing <UserProfile> stepper with:
//   - A welcoming intro screen explaining the value of filling it out
//   - A "Maybe later" / skip option that records the dismissal so
//     we don't re-prompt every session
//   - On completion or skip, the dialog closes and the user lands on
//     the chat tab as normal
//
// Trigger logic lives in App.tsx — this component just renders.

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Slide,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  ExploreOutlined,
  AutoAwesomeOutlined,
  PsychologyOutlined,
} from '@mui/icons-material';
import { UserProfile } from '../profile/UserProfile';
import { useAuthStore } from '../../stores/authStore';
import { useT } from '../../i18n/useT';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const OnboardingDialog: React.FC<Props> = ({ open, onClose }) => {
  const t = useT();
  const { profile, updateUserProfile } = useAuthStore();
  // Two phases: 'intro' (the welcome screen) → 'form' (the
  // <UserProfile> stepper). Reset to intro on each open so a user
  // who skipped and then re-opened doesn't land mid-form.
  const [phase, setPhase] = useState<'intro' | 'form'>('intro');
  const [skipping, setSkipping] = useState(false);

  React.useEffect(() => {
    if (open) setPhase('intro');
  }, [open]);

  /** Mark onboarding as skipped on the profile so we don't re-prompt
   *  next session. Errors are non-fatal — worst case the user sees
   *  the prompt again later. */
  const handleSkip = async () => {
    setSkipping(true);
    try {
      const existing = profile?.travelProfile;
      // Only send `travelProfile` — updateUserProfile merges with
      // currentProfile and adds lastActive itself. Spreading the
      // full profile here would re-write fields that aren't allowed
      // by the Firestore update rule's affectedKeys.hasOnly check.
      await updateUserProfile({
        travelProfile: {
          ...(existing || {
            demographics: {
              ageRange: null,
              gender: null,
              homeCountry: null,
              primaryLanguage: null,
            },
            travelPreferences: {
              travelStyle: [],
              pace: null,
              foodPreferences: [],
              dietaryRestrictions: null,
            },
            interests: {
              cultural: [],
              nature: [],
              urban: [],
              adventure: [],
              wellness: [],
              familyFriendly: [],
            },
            constraints: {
              budgetRange: null,
              timeAvailability: null,
              accessibilityNeeds: null,
              safetyConsiderations: null,
            },
            completedAt: null,
            lastUpdated: new Date(),
          }),
          skippedOnboardingAt: new Date(),
          lastUpdated: new Date(),
        },
      });
    } catch (err) {
      console.warn('Failed to record onboarding skip:', err);
    } finally {
      setSkipping(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        // Backdrop / Esc close = treat as skip so we don't re-prompt
        if (phase === 'intro') handleSkip();
        else onClose();
      }}
      fullScreen
      TransitionComponent={SlideUp}
      PaperProps={{ sx: { bgcolor: 'background.default' } }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {phase === 'intro' ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              maxWidth: 480,
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <AutoAwesomeOutlined sx={{ fontSize: 36 }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
              
              {t("Let's personalize your guide")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 4, lineHeight: 1.6 }}
            >
              
              {t("Answer a few questions about how you like to travel. It takes about two minutes and helps me give you recommendations that actually fit you — not generic ones.")}
            </Typography>

            {/* Three value props — what they get out of finishing */}
            <Stack spacing={2.5} sx={{ width: '100%', mb: 4, textAlign: 'left' }}>
              <ValueProp
                icon={<PsychologyOutlined />}
                title={t("Recommendations that fit you")}
                body={t("I'll skip the things you'd never enjoy and surface what you'd actually love.")}
              />
              <ValueProp
                icon={<ExploreOutlined />}
                title={t("Better answers in chat")}
                body={t("When I know your travel style and dietary preferences, the suggestions get sharper.")}
              />
              <ValueProp
                icon={<AutoAwesomeOutlined />}
                title={t("A guide feed worth checking")}
                body={t("Your daily feed gets tailored to your interests, pace, and budget.")}
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ width: '100%' }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => setPhase('form')}
                fullWidth
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                
                {t("Get started")}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={handleSkip}
                disabled={skipping}
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {skipping ? t('Just a moment…') : t('Maybe later')}
              </Button>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 3 }}
            >
              
              {t("You can always update your profile later from the menu.")}
            </Typography>
          </Box>
        ) : (
          // Form phase — render the existing UserProfile stepper.
          // It writes to Firestore on Save and calls onClose, which
          // closes the entire dialog.
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <UserProfile onClose={onClose} />
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                p: 1.5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleSkip}
                disabled={skipping}
                sx={{ textTransform: 'none' }}
              >
                {skipping ? t('Just a moment…') : t("Skip — I'll do this later")}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ValueProp: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        bgcolor: 'action.hover',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {body}
      </Typography>
    </Box>
  </Stack>
);
